<template>
    <div class="payment-fields" :class="!hideFields && 'bank-debit-form'">
        <div v-if="!hideFields" class="payment-field payment-field__full">
            <v-text-field
                v-model="localData.totally"
                light
                outlined
                dense
                type="number"
                name="totally"
                :label="$t('pages.payments.totalAmount')"
                @input="handleTotalAmountChange"
            ></v-text-field>
            <v-checkbox
                v-model="isMultiplePayments"
                :label="$t('pages.payments.multiplePayments')"
                dense
                class="payment-field__checkbox"
            ></v-checkbox>
        </div>

        <div v-else class="payment-field__block">
            <small class="small">{{ $t('pages.payments.totalAmount') }}</small>
            {{ localData.totally }}
        </div>

        <template v-if="!hideFields && isMultiplePayments">
            <v-text-field
                v-model="localData.amount"
                :rules="[valid.amount]"
                light
                outlined
                dense
                type="number"
                name="amount"
                :label="$t('pages.payments.enterFirstAmount')"
                class="payment-field"
                @input="emitUpdate"
            ></v-text-field>

            <v-text-field
                v-model="quantityDisplay"
                :rules="[valid.quantity]"
                light
                outlined
                dense
                type="number"
                name="quantity"
                :label="$t('pages.payments.quantityOfPayments')"
                class="payment-field"
                @input="emitUpdate"
            ></v-text-field>
        </template>

        <template v-else-if="hideFields && isMultiplePayments">
            <div v-if="localData.amount" class="payment-field__block">
                <small class="small">{{
                    $t('pages.payments.enterFirstAmount')
                }}</small>
                {{ localData.amount }}
            </div>
            <div v-if="localData.quantity" class="payment-field__block">
                <small class="small">{{
                    $t('pages.payments.quantityOfPayments')
                }}</small>
                {{ quantityDisplay }}
            </div>
        </template>

        <div class="payment-field__row">
            <v-text-field
                v-model="localData.due_date"
                :rules="[valid.due_date]"
                light
                outlined
                dense
                type="date"
                name="due_date"
                :label="$t('pages.payments.dueDate')"
                class="payment-field"
                @input="emitUpdate"
            ></v-text-field>

            <v-text-field
                v-model="localData.check_number"
                :rules="[valid.check_number]"
                light
                outlined
                dense
                type="text"
                name="check_number"
                :label="$t('pages.payments.enterERNReferenceNumber')"
                class="payment-field"
                @input="emitUpdate"
            ></v-text-field>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        totally: {
            type: [String, Number],
            default: null,
        },
        payment_type: {
            type: Number,
            default: null,
        },
        quantity: {
            type: [String, Number],
            default: null,
        },
        amount: {
            type: [String, Number],
            default: null,
        },
        due_date: {
            type: String,
            default: '',
        },
        check_number: {
            type: [String, Number],
            default: null,
        },
        sub_id: {
            type: [String, Number],
            default: null,
        },
        local_id: {
            type: [String, Number],
            default: null,
        },
        hideFields: {
            type: Boolean,
            default: false,
        },
        numberOfPayments: {
            type: Number,
            default: null,
        },
    },
    emits: ['update-payment'],

    data() {
        const hasMultiplePayments =
            this.quantity && parseInt(this.quantity, 10) > 1

        const defaultAmount =
            this.amount || (hasMultiplePayments ? null : this.totally)
        const defaultQuantity =
            this.quantity || (hasMultiplePayments ? null : 1)

        return {
            isMultiplePayments: hasMultiplePayments,
            localData: {
                amount: defaultAmount,
                due_date:
                    this.due_date || new Date().toISOString().slice(0, 10),
                check_number: this.check_number || null,
                totally: this.totally || null,
                quantity: defaultQuantity,
                sub_id: this.sub_id || null,
                local_id: this.local_id || null,
                payment_type: this.payment_type || null,
            },
        }
    },
    watch: {
        due_date(newValue) {
            this.localData.due_date = newValue
        },
        amount(newValue) {
            this.localData.amount = newValue
        },
        check_number(newValue) {
            this.localData.check_number = newValue
        },
        totally(newValue) {
            this.localData.totally = newValue
            if (!this.isMultiplePayments && newValue) {
                this.localData.amount = newValue
                this.emitUpdate()
            }
        },
        quantity(newValue) {
            if (newValue) {
                this.localData.quantity = newValue
            }
        },
        isMultiplePayments(newVal) {
            if (!newVal) {
                this.localData.quantity = 1
                this.localData.amount = this.localData.totally
            }

            this.emitUpdate()
        },
    },
    computed: {
        quantityDisplay: {
            get() {
                if (!this.numberOfPayments) return this.localData.quantity

                if (this.localData.quantity < 2) return this.localData.quantity

                return this.numberOfPayments
                    ? `${this.numberOfPayments} / ${this.localData.quantity}`
                    : null
            },
            set(value) {
                const parsedValue = parseInt(value, 10)
                if (!isNaN(parsedValue)) {
                    this.localData.quantity = parsedValue
                    this.emitUpdate()
                }
            },
        },
        valid() {
            const requiredField = (v) =>
                !!v || this.$t('pages.payments.fieldIsRequired')

            const baseRules = {
                due_date: requiredField,
                check_number: requiredField,
            }

            if (this.isMultiplePayments) {
                baseRules.amount = requiredField
                baseRules.quantity = requiredField
            }

            return baseRules
        },
    },
    methods: {
        emitUpdate() {
            const dataToEmit = { ...this.localData }

            if (!this.isMultiplePayments) {
                dataToEmit.amount = dataToEmit.totally || dataToEmit.amount
                dataToEmit.quantity = 1
            }

            this.$emit('update-payment', dataToEmit)
        },
        handleTotalAmountChange() {
            if (!this.isMultiplePayments && this.localData.totally) {
                this.localData.amount = this.localData.totally
            }
            this.emitUpdate()
        },
    },
}
</script>

<style lang="scss">
.bank-debit-form {
    display: grid !important;
    grid-template-columns: repeat(2, 1fr) !important;
    column-gap: 20px !important;
}

.payment-field__full {
    grid-column: 1 / -1;
    display: flex;
    align-items: flex-start;
    gap: 16px;

    .v-text-field {
        flex: 1;
    }
}

.payment-field__checkbox {
    margin-top: 4px !important;
    flex-shrink: 0;
}

.payment-field__row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 20px;
    grid-column: 1 / -1;
}

.payment-field__block {
    text-wrap: nowrap;
}
</style>
